<template>
  <div class="weektype-wrap">
    <div class="weektype-actions">
      <button class="btn btn-primary" @click="validateAndDuplicate" :disabled="isSaving">
        Valider et dupliquer sur 6 mois
      </button>
    </div>
    <FullCalendar ref="fullCalendar" :options="calendarOptions" />

    <div class="page-block-overlay" v-if="isSaving">
      <div class="page-block-modal">
        <LoaderCar width="300px" marginTop="0" />
        <p class="page-block-text">Enregistrement de la semaine type en cours, merci de patienter...</p>
      </div>
    </div>
    
    <Dialog
      v-model:visible="showConflictDialog"
      modal
      header="Conflit détecté"
      :style="{ width: '460px', maxWidth: '95vw' }"
    >
      <div class="conflict-dialog">
        <div class="conflict-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Conflit détecté
        </div>
        <p class="conflict-message">{{ conflictMessage }}</p>
        <div class="conflict-actions">
          <button class="conflict-btn conflict-btn--ghost" @click="showConflictDialog = false; showConflictDetailDialog = true;">Détails</button>
          <button class="conflict-btn conflict-btn--primary" @click="showConflictDialog = false">Fermer</button>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="showConflictDetailDialog"
      modal
      header="Créneaux en conflit"
      :style="{ width: '640px', maxWidth: '95vw' }"
    >
      <table class="conflict-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Début</th>
            <th>Fin</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in conflictList" :key="c.type + '-' + c.id">
            <td><span class="conflict-type-badge" :class="'conflict-type-badge--' + c.typeClass">{{ c.type }}</span></td>
            <td>{{ c.start }}</td>
            <td>{{ c.end }}</td>
          </tr>
        </tbody>
      </table>
    </Dialog>
  </div>
</template>

<script>
import LoaderCar from "../../LoaderCar.vue";
import "@fullcalendar/core/vdom";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";

export default {

  props: [
    "moniteur",
  ],

  components: {
    FullCalendar,
    LoaderCar,
  },

  data() {
    return {
      isSaving: false,
      showConflictDialog: false,
      showConflictDetailDialog: false,
      conflictMessage: "",
      conflictData: { apointments: [], exams: [], examsBlanc: [] },
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: "timeGridWeek",
        nowIndicator: true,
        eventResizableFromStart: true,
        firstDay: 1,
        allDaySlot: true,
        slotMinTime: "07:00:00",
        slotMaxTime: "21:00:00",
        height: "auto",
        locale: "fr",
        dayHeaderFormat: { weekday: "long" },
        selectable: true,
        timeZone: "local",
        eventClick: this.deleteEvent,
        headerToolbar: false,

        events: [],
        selectAllow: this.selectAllow,
        select: this.select,
        editable: true,
        eventResize: this.eventResize,
        eventDrop: this.eventDrop,
      },
    };
  },

  computed: {
    heuresDispoSemaine() {
      if (this.moniteur == null) {
        return null;
      }

      let totalMinutesParJour = moment.duration(this.calendarOptions.slotMaxTime)
        .subtract(moment.duration(this.calendarOptions.slotMinTime))
        .asMinutes();
      let totalMinutesSemaine = totalMinutesParJour * 7;

      let indispoMinutes = 0;
      (this.calendarOptions.events || []).forEach((e) => {
        indispoMinutes += moment(e.end).diff(moment(e.start), "minutes");
      });

      return Math.max(0, totalMinutesSemaine - indispoMinutes) / 60;
    },

    conflictList() {
      let list = [];

      (this.conflictData.apointments || []).forEach((c) => {
        list.push({
          id: c.id,
          type: "RDV",
          typeClass: "rdv",
          start: moment(c.start.date).format("DD/MM/YYYY HH:mm"),
          end: moment(c.end.date).format("DD/MM/YYYY HH:mm"),
        });
      });

      (this.conflictData.exams || []).forEach((c) => {
        list.push({
          id: c.id,
          type: "Examen",
          typeClass: "exam",
          start: moment(c.start.date).format("DD/MM/YYYY HH:mm"),
          end: moment(c.end.date).format("DD/MM/YYYY HH:mm"),
        });
      });

      (this.conflictData.examsBlanc || []).forEach((c) => {
        list.push({
          id: c.id,
          type: "Examen blanc",
          typeClass: "exam-blanc",
          start: moment(c.start.date).format("DD/MM/YYYY HH:mm"),
          end: moment(c.end.date).format("DD/MM/YYYY HH:mm"),
        });
      });

      return list;
    },
  },

  watch: {
    moniteur: {
      immediate: true,
      handler(moniteur) {
        this.calendarOptions.events = [];

        if (moniteur == null) {
          return;
        }

        this.fetchSemaineType(moniteur);
      },
    },

    heuresDispoSemaine: {
      immediate: true,
      handler(v) {
        this.$emit("heuresDispoChange", v);
      },
    },
  },

  methods: {

    validateAndDuplicate() {
      if (this.moniteur == null) {
        return;
      }

      if (!window.confirm("Êtes-vous sûr de vouloir valider et dupliquer cette semaine type sur 6 mois ? Cela remplacera la semaine type actuelle du moniteur.")) {
        return;
      }

      this.submitSemaineType(false);
    },

    submitSemaineType(force) {
      this.isSaving = true;
      window.api
        .post(
          "/moniteur/" + this.moniteur.idMono + "/semaine-type",
          {
            planning: this.calendarOptions.events,
            force,
          },
          {
            headers: {
              Authorization: "Bearer " + this.$store.state.token,
            },
          }
        )
        .then(() => {
          this.isSaving = false;
          alert("Semaine type enregistrée");
          location.reload();
        })
        .catch((error) => {
          this.isSaving = false;
          if (error.response && error.response.status === 409 && error.response.data.conflict) {
            let message = error.response.data.message.replace(
              "Voulez-vous vraiment continuer ? Cela annulera automatiquement ces créneaux.",
              ""
            ).trim();
            this.conflictMessage = message + " Veuillez annuler ces créneaux vous-même avant de modifier la semaine type.";
            this.conflictData = error.response.data.conflicts || { apointments: [], exams: [], examsBlanc: [] };
            this.showConflictDialog = true;
            return;
          }
          console.log(error);
        });
    },

    fetchSemaineType(moniteur) {
      window.api
        .get("/moniteur/" + moniteur.idMono + "/semaine-type", {
          headers: {
            Authorization: "Bearer " + this.$store.state.token,
          },
        })
        .then((response) => {
          this.calendarOptions.events = this.projectOnCurrentWeek(response.data.planning);
        })
        .catch((error) => {
          console.log(error);
        });
    },

    projectOnCurrentWeek(planning) {
      let events = [];

      (planning || []).forEach((r) => {
        let now = new Date();
        let todayDay = now.getDay();
        let startDay = new Date(r.start).getDay();
        let endDay = new Date(r.end).getDay();
        let diffStart = now.getDate() - todayDay + (todayDay == 0 ? -6 : startDay);
        let diffEnd = now.getDate() - todayDay + (todayDay == 0 ? -6 : endDay);

        let startDate = new Date(now);
        startDate.setDate(diffStart);
        if (startDay == 0) {
          startDate.setDate(startDate.getDate() + 7);
        }

        let endDate = new Date(now);
        endDate.setDate(diffEnd);
        if (endDay == 0) {
          endDate.setDate(endDate.getDate() + 7);
        }

        let start = moment(startDate).format("YYYY-MM-DD") + "T" + r.start.split("T")[1];
        let end = moment(endDate).format("YYYY-MM-DD") + "T" + r.end.split("T")[1];

        events.push({
          id: r.id,
          title: "INDISPO",
          start: moment(start).format("YYYY-MM-DDTHH:mm:ss"),
          end: moment(end).format("YYYY-MM-DDTHH:mm:ss"),
          recurring: "true",
          color: "#494949FF",
          type: "semaineType",
        });
      });

      return events;
    },

    eventResize(info) {
      let ev = this.calendarOptions.events.find((e) => e.id == info.event["_def"].publicId);
      if (ev) {
        ev.start = moment(info.event.start).format("YYYY-MM-DDTHH:mm:ss");
        ev.end = moment(info.event.end).format("YYYY-MM-DDTHH:mm:ss");
      }
    },

    eventDrop(info) {
      let ev = this.calendarOptions.events.find((e) => e.id == info.event["_def"].publicId);
      if (ev) {
        ev.start = moment(info.event.start).format("YYYY-MM-DDTHH:mm:ss");
        ev.end = moment(info.event.end).format("YYYY-MM-DDTHH:mm:ss");
      }
    },

    deleteEvent(e) {
      let events = [];
      this.calendarOptions.events.forEach((i) => {
        if (i.id != e["event"]["_def"]["publicId"]) {
          events.push(i);
        }
      });
      this.calendarOptions.events = events;
    },

    selectAllow() {
      return this.moniteur != null;
    },

    select(e) {
      if (this.moniteur == null) {
        return;
      }

      let count = this.calendarOptions.events.length;

      if (count === 0) {
        count = count + 1;
      } else {
        count = this.calendarOptions.events[this.calendarOptions.events.length - 1].id + 1;
      }

      this.calendarOptions.events.push({
        id: count,
        title: "INDISPO",
        start: moment(e.startStr).format("YYYY-MM-DDTHH:mm:ss"),
        end: moment(e.endStr).format("YYYY-MM-DDTHH:mm:ss"),
        recurring: "true",
        color: "#494949FF",
        type: "semaineType",
      });
    },
  },
};
</script>

<style scoped>

div.weektype-wrap {
  position: relative;
}

div.weektype-actions {
  display: flex;
  justify-content: center;
  padding: 10px 12px;
}

div.page-block-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: wait;
}

div.page-block-modal {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

p.page-block-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  text-align: center;
}

:deep(.fc-day-today) {
  background-color: #fffde7 !important;
}

:deep(.fc-col-header-cell) {
  padding: 8px 0;
  font-size: 13px;
}

:deep(.fc-col-header-cell-cushion) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  color: #555;
  font-weight: 500;
  text-transform: capitalize;
}

:deep(.fc-day-today .fc-col-header-cell-cushion) {
  color: #1a73e8;
}

:deep(.fc-event) {
  border-radius: 6px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 2px 6px !important;
  box-shadow: none !important;
}

:deep(.fc-event-time) {
  font-size: 10px;
  display: block;
}

:deep(.fc-event-title) {
  font-weight: 600;
  display: block;
}

:deep(.fc-timegrid-slot) {
  height: 40px;
}

:deep(.fc-timegrid-slot-label) {
  font-size: 12px;
  color: #888;
}

:deep(.fc-scrollgrid) {
  border-color: #e0e0e0 !important;
}

:deep(.fc-scrollgrid td),
:deep(.fc-scrollgrid th) {
  border-color: #e0e0e0 !important;
}

:deep(.fc-toolbar) {
  padding: 8px 12px;
}

:deep(.fc-button-group),
:deep(.fc-button) {
  box-shadow: none !important;
  outline: none !important;
}

:deep(.fc-prev-button),
:deep(.fc-next-button) {
  background-color: transparent !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 50% !important;
  color: #555 !important;
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.fc-prev-button:hover),
:deep(.fc-next-button:hover) {
  background-color: #f1f3f4 !important;
}

:deep(.fc-timeGridWeek-button),
:deep(.fc-timeGridDay-button) {
  background-color: transparent !important;
  border: none !important;
  border-radius: 20px !important;
  color: #555 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 4px 14px !important;
}

:deep(.fc-timeGridWeek-button:hover),
:deep(.fc-timeGridDay-button:hover) {
  background-color: #f1f3f4 !important;
}

:deep(.fc-timeGridWeek-button.fc-button-active),
:deep(.fc-timeGridDay-button.fc-button-active) {
  background-color: #e8f0fe !important;
  border-color: #e8f0fe !important;
  color: #1a73e8 !important;
}

:deep(.fc-toolbar-chunk:last-child) {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  padding: 2px 4px;
}

</style>

<style>
/* Non-scoped : les Dialogs de conflit téléportent leur contenu vers document.body,
   ce qui échappe au CSS scoped ci-dessus. */
div.conflict-dialog {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

div.conflict-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  font-size: 13px;
  font-weight: 700;
  width: fit-content;
}

p.conflict-message {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin: 0;
}

div.conflict-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

button.conflict-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

button.conflict-btn--ghost {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #64748b;
}
button.conflict-btn--ghost:hover {
  background: #f8fafc;
}

button.conflict-btn--primary {
  background: #3b82f6;
  color: #fff;
}
button.conflict-btn--primary:hover {
  background: #2563eb;
}

table.conflict-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

table.conflict-table th {
  text-align: left;
  padding: 10px 14px;
  background: #ffffff;
  color: #9ca3af;
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid #e5e7eb;
}

table.conflict-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
}

span.conflict-type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

span.conflict-type-badge--rdv {
  background: #e0e7ff;
  color: #4338ca;
}

span.conflict-type-badge--exam {
  background: #fce7f3;
  color: #a21caf;
}

span.conflict-type-badge--exam-blanc {
  background: #fff7ed;
  color: #c2410c;
}
</style>
